import {parseStorageScan} from './storageModel.mjs';
import {certificateScanKey} from './pickRunModel.mjs';

export const REMOVAL_REASONS = ['other', 'trade', 'auction', 'grandium', 'set_chase', 'sale'];

export function normalizeRemovalScan(raw) {
  if (typeof raw !== 'string') return null;
  const parsed = parseStorageScan(raw);
  return parsed?.kind === 'cert' && certificateScanKey(parsed.certNumber) ? parsed.certNumber : null;
}

export const removalScanKey = raw => {
  const cert = normalizeRemovalScan(raw);
  return cert ? certificateScanKey(cert) : null;
};

export function addRemovalScan(rows, raw) {
  const scan = normalizeRemovalScan(raw), key = removalScanKey(scan);
  if (!key) return {rows, added:false, error:'Scan or type the saved slab cert number.', key:null};
  if (rows.some(row => row.key === key || removalScanKey(row.certNumber || row.scan) === key)) {
    return {rows, added:false, error:'', key};
  }
  return {rows:[...rows, {key, scan, certNumber:scan, status:'waiting'}], added:true, error:'', key};
}

// Persist scans, not stale stock or positions. Every restored scan is looked up
// again before the operator can confirm a physical removal.
export function restoreRemovalDraft(raw) {
  let value;
  try { value = typeof raw === 'string' ? JSON.parse(raw) : raw; } catch { value = null; }
  const rows = [];
  if (Array.isArray(value?.rows)) for (const saved of value.rows) {
    const next = addRemovalScan(rows, typeof saved === 'string' ? saved : saved?.scan || saved?.certNumber);
    if (next.added) rows.push(next.rows[next.rows.length - 1]);
  }
  return {
    rows,
    reason:REMOVAL_REASONS.includes(value?.reason) ? value.reason : 'other',
    reference:typeof value?.reference === 'string' ? value.reference.replace(/[\u0000-\u001f\u007f]/g, '').slice(0, 200) : '',
  };
}

export function buildRemovalCommand({rows, reason, reference, requestId}) {
  if (!Array.isArray(rows) || !rows.length) throw Error('Scan at least one slab first.');
  if (!REMOVAL_REASONS.includes(reason)) throw Error('Choose what these slabs are for.');
  if (typeof reference !== 'string' || !reference.trim() || reference.length > 200 || /[\u0000-\u001f\u007f]/.test(reference)) {
    throw Error('Enter who is taking the slabs or where they are going.');
  }
  if (typeof requestId !== 'string' || !/^[a-zA-Z0-9_-]{8,64}$/.test(requestId)) throw Error('The removal reference is invalid.');
  const certs = [], seen = new Set(), expectedRevisions = {};
  for (const row of rows) {
    if (row.status !== 'ready' || !row.slab?.id || !normalizeRemovalScan(row.slab.certNumber)
      || removalScanKey(row.scan) !== removalScanKey(row.slab.certNumber)) {
      throw Error('Wait for every slab check and resolve any flagged scans before checking out.');
    }
    const slab = row.slab, key = removalScanKey(slab.certNumber), location = slab.location;
    if (seen.has(key)) throw Error('Remove the duplicate slab before checking out.');
    if (slab.stockStatus !== 'in_stock') throw Error('A slab is already allocated or out of inventory. Check its existing pick.');
    if (!location?.box || !Number.isSafeInteger(location.revision) || location.revision < 0
      || !['L','R'].includes(location.side) || !Number.isSafeInteger(location.position) || location.position < 1) {
      throw Error('Every slab needs a confirmed box position before checking out.');
    }
    if (location.issueBlocked || location.transferPending || ['offsite','in_transit','prepared'].includes(location.custodyState)) {
      throw Error('A slab has a location issue or its container is locked. Check it before removing it.');
    }
    if (Object.hasOwn(expectedRevisions, location.box) && expectedRevisions[location.box] !== location.revision) {
      throw Error('A box changed while you scanned. Refresh the checks before checking out.');
    }
    expectedRevisions[location.box] = location.revision;
    seen.add(key); certs.push(slab.certNumber);
  }
  return {operation:'batchCheckout', requestId, certs, reason, reference:reference.trim(), confirmed:true, expectedRevisions};
}

export function confirmedRemovalReceipt(receipt, command) {
  if (receipt?.saved !== true || receipt.requestId !== command?.requestId
    || receipt.count !== command.certs.length || !Array.isArray(receipt.certs) || !Array.isArray(receipt.pickIds)
    || receipt.certs.length !== command.certs.length || receipt.pickIds.length !== command.certs.length) return false;
  const received = new Set(receipt.certs), requested = new Set(command.certs);
  return received.size === requested.size && requested.size === command.certs.length
    && command.certs.every(cert => received.has(cert))
    && new Set(receipt.pickIds).size === receipt.pickIds.length
    && receipt.pickIds.every(id => typeof id === 'string' && /^[a-f0-9]{8}(?:-[a-f0-9]{4}){3}-[a-f0-9]{12}$/i.test(id));
}
